import { useEffect, useState } from 'react';
import { Calendar, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Calendar as CalendarType } from '../types/calendar';
import { getBannerStyle } from '../lib/banner/utils';
import CategoryBadge from '../components/CategoryBadge';
import SEO from '../components/SEO';

export default function Examples() {
  const [calendars, setCalendars] = useState<CalendarType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentCalendars();
  }, []);

  const fetchRecentCalendars = async () => {
    try {
      const { data } = await supabase
        .from('calendars')
        .select(`
          *,
          profiles!calendars_user_id_fkey (
            display_name
          ),
          calendar_stats (
            subscriber_count
          )
        `)
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(20);

      if (data) {
        setCalendars(data);
      }
    } catch (error) {
      console.error('Error fetching recent calendars:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Recent Calendars - Calenlist"
        description="Browse the most recently created public calendars on Calenlist."
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Recent Calendars</h1>
          <p className="text-gray-500">The 20 most recently created public calendars on Calenlist</p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <Calendar className="h-10 w-10 text-gray-300 mx-auto animate-pulse" />
            <p className="mt-4 text-gray-400">Loading calendars...</p>
          </div>
        ) : calendars.length > 0 ? (
          <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {calendars.map(calendar => {
              const bannerStyle = getBannerStyle(calendar.banner);
              const subscriberCount =
                calendar.calendar_stats?.[0]?.subscriber_count ?? 0;

              return (
                <Link
                  key={calendar.id}
                  to={`/calendar/${calendar.id}`}
                  className="block group"
                >
                  <div
                    className="rounded-lg p-5 transition-shadow group-hover:shadow-md h-[240px] flex flex-col"
                    style={{ backgroundColor: bannerStyle.backgroundColor }}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <h3
                        className="text-base font-semibold leading-snug line-clamp-2 flex-1 mr-2"
                        style={{ color: bannerStyle.color }}
                      >
                        {calendar.name}
                      </h3>
                    </div>

                    {calendar.profiles?.display_name && (
                      <p
                        className="text-xs mb-2 opacity-70"
                        style={{ color: bannerStyle.color }}
                      >
                        by {calendar.profiles.display_name}
                      </p>
                    )}

                    {calendar.category_id && (
                      <div className="mb-2">
                        <CategoryBadge categoryId={calendar.category_id} size="sm" />
                      </div>
                    )}

                    {calendar.description && (
                      <p
                        className="text-xs line-clamp-3 flex-grow opacity-80"
                        style={{ color: bannerStyle.color }}
                      >
                        {calendar.description}
                      </p>
                    )}

                    <div className="mt-auto flex items-center pt-2">
                      <Users
                        className="h-3.5 w-3.5 mr-1 opacity-60"
                        style={{ color: bannerStyle.color }}
                      />
                      <span
                        className="text-xs opacity-60"
                        style={{ color: bannerStyle.color }}
                      >
                        {subscriberCount.toLocaleString()} subscriber{subscriberCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20">
            <Calendar className="h-10 w-10 text-gray-300 mx-auto" />
            <p className="mt-4 text-gray-400">No public calendars found.</p>
          </div>
        )}
      </div>
    </div>
  );
}
